import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AgentChatMessage, AgentConversation } from './Agent.types';
import { addChat, getChats, getAgentConfig, createChat } from './Agent.actions';
import type { AgentConfig } from 'configuration-schemas/viewdefns/viewdefn';
import { isEmpty } from 'lodash';

export interface AgentState {
  freeformAsyncState:
  | 'loading'
  | 'unloaded'
  | 'loaded'
  | 'error';
  actionMode: 'automatic' | 'user';
  conversations: AgentConversation[];
  viewDefn: AgentConfig | null;
}

const initAgentState: AgentState = {
  freeformAsyncState: 'unloaded',
  actionMode: 'user',
  conversations: [],
  viewDefn: null,
};

type AgentChatPayload = { convoId: string; message: AgentChatMessage }

export const agentSlice = createSlice({
  name: 'agent',
  initialState: initAgentState,
  reducers: {
    resetAgent: () => initAgentState,
    setActionMode: (state, action: PayloadAction<'automatic' | 'user'>) => {
      state.actionMode = action.payload;
    },
    receiveConversations: (state, action: PayloadAction<AgentConversation[]>) => {
      state.conversations = action.payload;
    },
    receiveAgentMessage: (state, action: PayloadAction<{
      user: string;
      convoId: string;
      message: string;
      additionalData?: any[];
    }>) => {
      const conversation = state.conversations.find(c => c.convoId === action.payload.convoId);
      if (conversation) {
        const newMessage = {
          message: action.payload.message,
          type: 'agent' as const,
          additionalData: action.payload.additionalData,
        };
        conversation.messages.push(newMessage);
      }
    },
  },
  extraReducers: (builder) => {
    // #region "getChats"
    builder.addCase(getChats.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(getChats.fulfilled, (state, { payload }) => {
      if (isEmpty(state.conversations)) {
        state.conversations = payload;
      }
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(getChats.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion
    // #region "addChat"
    builder.addCase(addChat.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(addChat.fulfilled, (state, action: { payload: AgentChatPayload }) => {
      const conversation = state.conversations.find(c => c.convoId === action.payload.convoId);
      if (conversation) {
        if (!conversation.messages) {
          conversation.messages = [action.payload.message];
        } else {
          conversation.messages.push(action.payload.message);
        }
      }
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(addChat.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion

    // #region "getAgentConfig"
    builder.addCase(getAgentConfig.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(getAgentConfig.fulfilled, (state, { payload }) => {
      state.viewDefn = payload;
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(getAgentConfig.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion

    // #region "createChat"
    builder.addCase(createChat.pending, (state) => {
      state.freeformAsyncState = 'loading';
    });
    builder.addCase(createChat.fulfilled, (state, { payload }) => {
      const newConversation: AgentConversation = {
        convoId: payload.convoId,
        messages: [],
      };
      state.conversations.push(newConversation);
      state.freeformAsyncState = 'loaded';
    });
    builder.addCase(createChat.rejected, (state) => {
      state.freeformAsyncState = 'error';
    });
    // #endregion
  },
});

export const { resetAgent, setActionMode, receiveConversations, receiveAgentMessage } = agentSlice.actions;

export default agentSlice.reducer;
